/**
 * Validation Utilities
 * Form validation functions following Epic 1.1.1 requirements
 */

/**
 * Validate password strength
 * Requirement: At least 8 characters with uppercase, lowercase, and number
 */
export const validatePassword = (password) => {
  if (!password || password.length < 8) {
    return {
      valid: false,
      message: 'Password must be at least 8 characters long'
    };
  }

  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (!hasUppercase || !hasLowercase || !hasNumber) {
    return {
      valid: false,
      message: 'Password must contain uppercase, lowercase, and number'
    };
  }

  if (!hasSpecial) {
    return {
      valid: false,
      message: 'Password must contain at least one special character'
    };
  }

  return { valid: true, message: '' };
};

/**
 * Validate email format
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate name (first or last)
 */
export const validateName = (name) => {
  return !!(name && name.trim().length >= 2);
};

/**
 * Validate phone number (US format)
 * Accepts various formats: (555) 123-4567, 555-123-4567, 5551234567
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid 10-digit US phone number
 */
export const validatePhone = (phone) => {
  if (!phone) {return false;}

  // Strip all non-digits
  let cleaned = phone.replace(/\D/g, '');

  // Strip leading 1 (US country code) if present and there are 11 digits
  if (cleaned.length === 11 && cleaned[0] === '1') {
    cleaned = cleaned.substring(1);
  }

  // Must be exactly 10 digits
  return cleaned.length === 10;
};

/**
 * Validate phone number with detailed error message
 * @param {string} phone - Phone number to validate
 * @returns {object} { valid: boolean, message: string }
 */
export const validatePhoneDetailed = (phone) => {
  if (!phone || phone.trim() === '') {
    return {
      valid: false,
      message: 'Phone number is required'
    };
  }

  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 11 && cleaned[0] === '1') {
    // Has country code
    return {
      valid: cleaned.substring(1).length === 10,
      message: cleaned.substring(1).length === 10 ? '' : 'Must be 10 digits after country code'
    };
  }

  if (cleaned.length !== 10) {
    return {
      valid: false,
      message: `Must be 10 digits (found ${cleaned.length})`
    };
  }

  return { valid: true, message: '' };
};

/**
 * Validate date is in the future
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is in the future
 */
export const validateFutureDate = (dateString) => {
  if (!dateString) {return false;}

  // Compare date strings directly to avoid timezone issues
  const today = new Date().toISOString().split('T')[0];
  return dateString >= today;
};

/**
 * Validate date is in the future with detailed error
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {object} { valid: boolean, message: string }
 */
export const validateFutureDateDetailed = (dateString) => {
  if (!dateString) {
    return {
      valid: false,
      message: 'Please select a date'
    };
  }

  const selectedDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (isNaN(selectedDate.getTime())) {
    return {
      valid: false,
      message: 'Invalid date format'
    };
  }

  if (selectedDate < today) {
    return {
      valid: false,
      message: 'Date must be in the future'
    };
  }

  return { valid: true, message: '' };
};

/**
 * Validate party size (number of guests)
 * @param {number} size - Party size
 * @param {number} min - Minimum party size (default: 1)
 * @param {number} max - Maximum party size
 * @returns {boolean} True if within valid range
 */
export const validatePartySize = (size, min = 1, max) => {
  const num = parseInt(size, 10);

  if (isNaN(num) || num < min) {return false;}
  if (max && num > max) {return false;}

  return true;
};

/**
 * Validate party size with detailed error
 * @param {number} size - Party size
 * @param {number} min - Minimum party size (default: 1)
 * @param {number} max - Maximum party size
 * @returns {object} { valid: boolean, message: string }
 */
export const validatePartySizeDetailed = (size, min = 1, max) => {
  const num = parseInt(size, 10);

  if (isNaN(num)) {
    return {
      valid: false,
      message: 'Please enter a valid number'
    };
  }

  if (num < min) {
    return {
      valid: false,
      message: `Minimum ${min} ${min === 1 ? 'guest' : 'guests'}`
    };
  }

  if (max && num > max) {
    return {
      valid: false,
      message: `Maximum ${max} guests`
    };
  }

  return { valid: true, message: '' };
};

/**
 * Validate checkbox is checked (for terms/policies)
 * @param {boolean} checked - Checkbox state
 * @returns {boolean} True if checked
 */
export const validateCheckbox = (checked) => {
  return checked === true;
};

/**
 * Validate checkbox with custom error message
 * @param {boolean} checked - Checkbox state
 * @param {string} customMessage - Custom error message
 * @returns {object} { valid: boolean, message: string }
 */
export const validateCheckboxDetailed = (checked, customMessage = 'This field is required') => {
  return {
    valid: checked === true,
    message: checked ? '' : customMessage
  };
};

/**
 * Validate booking stage data
 * Helper for multi-stage booking flows
 * @param {number} stage - Stage number (1-7)
 * @param {object} bookingData - Booking form data
 * @returns {object} { valid: boolean, errors: object }
 */
export const validateBookingStage = (stage, bookingData) => {
  const errors = {};

  // Stage 1: Trip Details
  if (stage === 1) {
    if (!bookingData.startDate) {
      errors.startDate = 'Please select a date';
    } else if (!validateFutureDate(bookingData.startDate)) {
      errors.startDate = 'Date must be in the future';
    }

    if (!validatePartySize(bookingData.partySize, 1, bookingData.maxGuests)) {
      errors.partySize = bookingData.maxGuests
        ? `Party size must be between 1 and ${bookingData.maxGuests}`
        : 'Invalid party size';
    }
  }

  // Stage 2: Account Creation
  if (stage === 2) {
    if (!validateName(bookingData.firstName)) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!validateName(bookingData.lastName)) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    if (!validateEmail(bookingData.email)) {
      errors.email = 'Valid email address required';
    }

    const passwordValidation = validatePassword(bookingData.password);
    if (!passwordValidation.valid) {
      errors.password = passwordValidation.message;
    }
  }

  // Stage 3: Contact Information / SMS Consent
  if (stage === 3) {
    // Only validate phone if SMS consent is given
    if (bookingData.smsConsent) {
      if (!bookingData.phoneNumber || !bookingData.phoneNumber.trim()) {
        errors.phoneNumber = 'Phone number required when opting in to SMS';
      } else if (!validatePhone(bookingData.phoneNumber)) {
        errors.phoneNumber = 'Valid 10-digit phone number required';
      }
    }
  }

  // Stage 4: Age Verification
  if (stage === 4) {
    if (bookingData.minimumAge && bookingData.minimumAge > 0) {
      if (!bookingData.ageVerified) {
        errors.ageVerified = `You must confirm all participants meet the ${bookingData.minimumAge}+ age requirement`;
      }
    }
  }

  // Stage 5: Requirements (optional fields, no validation needed)
  // Stage 6: Terms & Policies
  if (stage === 6) {
    if (!validateCheckbox(bookingData.termsAccepted)) {
      errors.termsAccepted = 'You must accept the terms of service';
    }

    if (!validateCheckbox(bookingData.policyAccepted)) {
      errors.policyAccepted = 'You must accept the cancellation policy';
    }
  }

  // Stage 7: Payment (handled by payment processor)

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};
